import type {
  ParsedAngle,
  PiNumber,
} from "@/lib/tool-kit/geometry/angle-input";
import {
  addExact,
  type Exact,
  exactPi,
  exactPiPower,
  exactRational,
  exactSqrt,
  mulExact,
  scaleExact,
  toNumberExact,
} from "@/lib/tool-kit/geometry/exact";
import { exactTrigValues } from "@/lib/tool-kit/geometry/trig-exact";
import {
  DEFAULT_FORMAT,
  decimalText,
  exactText,
  type ResultFormat,
  text,
} from "@/lib/tool-kit/geometry/working";
import { divR, intR, mulR, signR } from "@/lib/tool-kit/math/rational";

/**
 * Circles: from any one of the radius, diameter, circumference or area to the other
 * three, and from a central angle to the arc, sector, chord and segment.
 *
 * Results stay exact wherever the input allows. A radius of 5 gives d = 10, C = 10π and
 * A = 25π; a circumference typed as a plain 10 gives r = 5/π, and one typed as 10π gives
 * r = 5. Only an area typed without π (√(A/π)) has no closed form here and falls back to
 * decimals.
 */

export const CIRCLE_GIVEN = [
  "radius",
  "diameter",
  "circumference",
  "area",
] as const;
export type CircleGiven = (typeof CIRCLE_GIVEN)[number];

/** A measurement: exact when it can be, always with a decimal. */
export interface Measure {
  exact: Exact | null;
  value: number;
}

export type CircleError = "positive" | "plain";

export interface CircleBasics {
  ok: true;
  radius: Measure;
  diameter: Measure;
  circumference: Measure;
  area: Measure;
  steps: string[];
}

export interface CircleFailure {
  ok: false;
  error: CircleError;
}

const TWO = intR(2);
const HALF = divR(intR(1), TWO);
const FULL_TURN_DEGREES = 360;
const ANGLE_TOLERANCE = 1e-9;
const BRACKET_NEEDED = /[/√+−]/;

function measureOf(exact: Exact): Measure {
  return { exact, value: toNumberExact(exact) };
}

/** An exact or decimal value written for a substitution: bracketed when composite. */
function inline(measure: Measure, format: ResultFormat): string {
  const written = measure.exact
    ? exactText(measure.exact, format)
    : decimalText(measure.value, format);
  return BRACKET_NEEDED.test(written) ? `(${written})` : written;
}

function plain(measure: Measure, format: ResultFormat): string {
  return measure.exact
    ? exactText(measure.exact, format)
    : decimalText(measure.value, format);
}

function equality(measure: Measure): string {
  return measure.exact ? "=" : "≈";
}

/** The radius from whatever was given. */
function radiusFrom(
  given: CircleGiven,
  input: PiNumber
): { radius: Measure; lines: (f: ResultFormat) => string[] } | CircleError {
  const k = input.coefficient;
  if (signR(k) <= 0) {
    return "positive";
  }
  if (given === "radius" || given === "diameter") {
    if (input.hasPi) {
      return "plain";
    }
    const r = given === "radius" ? k : mulR(k, HALF);
    return {
      radius: measureOf(exactRational(r)),
      lines: (f) =>
        given === "radius"
          ? []
          : [`r = d / 2 = ${text(k, f)} / 2 = ${text(r, f)}`],
    };
  }
  if (given === "circumference") {
    const radius = input.hasPi
      ? measureOf(exactRational(mulR(k, HALF)))
      : measureOf(exactPiPower(mulR(k, HALF), -1));
    return {
      radius,
      lines: (f) => {
        const typed = input.hasPi ? `${text(k, f)}π` : text(k, f);
        return [
          `r = C / (2π) = ${typed} / (2π) = ${plain(radius, f)}${radius.exact ? "" : ` ≈ ${decimalText(radius.value, f)}`}`,
        ];
      },
    };
  }
  // Area: r = √(A / π). Exact when A is a multiple of π.
  if (input.hasPi) {
    const radius = measureOf(exactSqrt(k));
    return {
      radius,
      lines: (f) => [
        `r = √(A / π) = √(${text(k, f)}π / π) = ${plain(radius, f)}`,
      ],
    };
  }
  const value = Math.sqrt(Number(k.num) / Number(k.den) / Math.PI);
  return {
    radius: { exact: null, value },
    lines: (f) => [
      `r = √(A / π) = √(${text(k, f)} / π) ≈ ${decimalText(value, f)}`,
    ],
  };
}

function derive(radius: Measure): Omit<CircleBasics, "ok" | "steps"> {
  if (radius.exact) {
    const r = radius.exact;
    return {
      radius,
      diameter: measureOf(scaleExact(r, TWO)),
      circumference: measureOf(mulExact(exactPi(TWO), r)),
      area: measureOf(mulExact(exactPi(intR(1)), mulExact(r, r))),
    };
  }
  const v = radius.value;
  return {
    radius,
    diameter: { exact: null, value: 2 * v },
    circumference: { exact: null, value: 2 * Math.PI * v },
    area: { exact: null, value: Math.PI * v * v },
  };
}

/** All four measurements of a circle, with the working. */
export function computeCircle(
  given: CircleGiven,
  input: PiNumber,
  format: ResultFormat = DEFAULT_FORMAT
): CircleBasics | CircleFailure {
  const start = radiusFrom(given, input);
  if (typeof start === "string") {
    return { ok: false, error: start };
  }
  const parts = derive(start.radius);
  const r = inline(parts.radius, format);
  const steps = [...start.lines(format)];
  if (given !== "diameter") {
    steps.push(
      `d = 2r = 2 × ${r} ${equality(parts.diameter)} ${plain(parts.diameter, format)}`
    );
  }
  if (given !== "circumference") {
    steps.push(
      `C = 2πr = 2 × π × ${r} ${equality(parts.circumference)} ${plain(parts.circumference, format)}`
    );
  }
  if (given !== "area") {
    steps.push(
      `A = πr² = π × ${r}² ${equality(parts.area)} ${plain(parts.area, format)}`
    );
  }
  return { ok: true, ...parts, steps };
}

export interface SectorParts {
  ok: true;
  arc: Measure;
  sector: Measure;
  chord: Measure;
  segment: Measure;
  sagitta: Measure;
  sectorPerimeter: Measure;
  segmentPerimeter: Measure;
  angleDegrees: number;
  steps: string[];
}

export interface SectorFailure {
  ok: false;
  error: "angle";
}

function negate(value: Exact): Exact {
  return scaleExact(value, intR(-1));
}

interface ExactPieces {
  sinHalf: Exact | null;
  cosHalf: Exact | null;
  sinFull: Exact | null;
}

function exactAngleParts(angle: ParsedAngle): ExactPieces {
  if (angle.degrees === null) {
    return { sinHalf: null, cosHalf: null, sinFull: null };
  }
  const half = exactTrigValues(divR(angle.degrees, TWO));
  const full = exactTrigValues(angle.degrees);
  const pick = (values: typeof half, fn: "sin" | "cos") => {
    const value = values?.[fn];
    return value === undefined || value === "undefined" ? null : value;
  };
  return {
    sinHalf: pick(half, "sin"),
    cosHalf: pick(half, "cos"),
    sinFull: pick(full, "sin"),
  };
}

interface ExactSector {
  arc: Exact | null;
  sector: Exact | null;
  chord: Exact | null;
  segment: Exact | null;
  sagitta: Exact | null;
  sectorPerimeter: Exact | null;
  segmentPerimeter: Exact | null;
}

const ONE_EXACT = exactRational(intR(1));

function exactSector(radius: Measure, angle: ParsedAngle): ExactSector {
  const r = radius.exact;
  const { sinHalf, cosHalf, sinFull } = exactAngleParts(angle);
  if (r === null) {
    return {
      arc: null,
      sector: null,
      chord: null,
      segment: null,
      sagitta: null,
      sectorPerimeter: null,
      segmentPerimeter: null,
    };
  }
  const rr = mulExact(r, r);
  const arc = mulExact(r, angle.radians);
  const chord = sinHalf ? scaleExact(mulExact(r, sinHalf), TWO) : null;
  return {
    arc,
    sector: scaleExact(mulExact(rr, angle.radians), HALF),
    chord,
    segment: sinFull
      ? scaleExact(mulExact(rr, addExact(angle.radians, negate(sinFull))), HALF)
      : null,
    sagitta: cosHalf ? mulExact(r, addExact(ONE_EXACT, negate(cosHalf))) : null,
    sectorPerimeter: addExact(scaleExact(r, TWO), arc),
    segmentPerimeter: chord ? addExact(arc, chord) : null,
  };
}

function measureOrDecimal(exact: Exact | null, value: number): Measure {
  return exact
    ? { exact, value: toNumberExact(exact) }
    : { exact: null, value };
}

function sectorSteps(
  radius: Measure,
  angle: ParsedAngle,
  parts: Pick<SectorParts, "arc" | "sector" | "chord" | "segment" | "sagitta">,
  format: ResultFormat
): string[] {
  const theta = angle.radiansValue;
  const degrees = angle.degreesValue;
  const rText = inline(radius, format);
  const thetaText = angle.radianText.includes("/")
    ? `(${angle.radianText})`
    : angle.radianText;
  const halfDegrees = angle.degrees
    ? `${text(divR(angle.degrees, TWO), format)}°`
    : `${decimalText(degrees / 2, format)}°`;
  const { sinHalf, sinFull } = exactAngleParts(angle);
  const sinHalfText = sinHalf
    ? exactText(sinHalf, format)
    : decimalText(Math.sin(theta / 2), format);
  const sinFullText = sinFull
    ? exactText(sinFull, format)
    : decimalText(Math.sin(theta), format);
  const eq = (measure: Measure) => (measure.exact ? "=" : "≈");
  const angleLine =
    angle.degrees === null
      ? `θ = ${angle.radianText} rad ≈ ${decimalText(degrees, format)}°`
      : `θ = ${text(angle.degrees, format)}° = ${angle.radianText} rad`;
  return [
    angleLine,
    `s = r × θ = ${rText} × ${thetaText} ${eq(parts.arc)} ${plain(parts.arc, format)}`,
    `Sector = ½ × r² × θ = ½ × ${rText}² × ${thetaText} ${eq(parts.sector)} ${plain(parts.sector, format)}`,
    `Chord = 2 × r × sin(θ / 2) = 2 × ${rText} × sin(${halfDegrees}) = 2 × ${rText} × ${sinHalfText} ${eq(parts.chord)} ${plain(parts.chord, format)}`,
    `Segment = ½ × r² × (θ − sin θ) = ½ × ${rText}² × (${angle.radianText} − ${sinFullText}) ${eq(parts.segment)} ${plain(parts.segment, format)}`,
    `Sagitta = r × (1 − cos(θ / 2)) ${eq(parts.sagitta)} ${plain(parts.sagitta, format)}`,
  ];
}

/** Arc length, sector area, chord, segment area and sagitta for a central angle. */
export function computeSectorParts(
  radius: Measure,
  angle: ParsedAngle,
  format: ResultFormat = DEFAULT_FORMAT
): SectorParts | SectorFailure {
  const degrees = angle.degreesValue;
  if (!(degrees > 0 && degrees <= FULL_TURN_DEGREES + ANGLE_TOLERANCE)) {
    return { ok: false, error: "angle" };
  }
  const rv = radius.value;
  const theta = angle.radiansValue;
  const exact = exactSector(radius, angle);
  const arcValue = rv * theta;
  const chordValue = 2 * rv * Math.sin(theta / 2);
  const parts = {
    arc: measureOrDecimal(exact.arc, arcValue),
    sector: measureOrDecimal(exact.sector, 0.5 * rv * rv * theta),
    chord: measureOrDecimal(exact.chord, chordValue),
    segment: measureOrDecimal(
      exact.segment,
      0.5 * rv * rv * (theta - Math.sin(theta))
    ),
    sagitta: measureOrDecimal(exact.sagitta, rv * (1 - Math.cos(theta / 2))),
    sectorPerimeter: measureOrDecimal(exact.sectorPerimeter, 2 * rv + arcValue),
    segmentPerimeter: measureOrDecimal(
      exact.segmentPerimeter,
      arcValue + chordValue
    ),
  };
  return {
    ok: true,
    ...parts,
    angleDegrees: degrees,
    steps: sectorSteps(radius, angle, parts, format),
  };
}
